import { useMemo, useState, type ChangeEvent, type DragEvent } from 'react';
import type { Enhancements, RenderParams } from '@/tools/image-engine';
import { enhanceWithAi } from './enhance-client';
import { DEFAULT_PRESET, presetById, type PresetId } from './presets';
import { IMAGE_STEPS, type ImageStep } from './Stepper';
import { useImageSession } from './use-image-session';

export type AiState =
  | { readonly status: 'idle' }
  | { readonly status: 'confirming' }
  | { readonly status: 'working' }
  | { readonly status: 'done'; readonly dataUrl: string }
  | { readonly status: 'error'; readonly message: 'unavailable' | 'failed' };

type Session = ReturnType<typeof useImageSession>;
type Format = 'jpeg' | 'png';

/** The chosen image as a file in `format`; a JPEG asked as PNG (or the AI's PNG asked as JPEG) is redrawn. */
async function toFormat(dataUrl: string, format: Format): Promise<Blob> {
  const type = format === 'png' ? 'image/png' : 'image/jpeg';
  if (dataUrl.startsWith(`data:${type}`)) return (await fetch(dataUrl)).blob();
  const image = new Image();
  image.src = dataUrl;
  await image.decode();
  const canvas = document.createElement('canvas');
  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;
  canvas.getContext('2d')?.drawImage(image, 0, 0);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, 0.92));
  canvas.width = 0;
  canvas.height = 0;
  if (!blob) throw new Error('Could not encode the image');
  return blob;
}

async function saveImage(result: string, format: Format, name: string): Promise<void> {
  const blob = await toFormat(result, format);
  const base = name.replace(/\.[^.]+$/, '');
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${base}-raddrizzata.${format === 'png' ? 'png' : 'jpg'}`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** The rotation, the preset and the sliders; `onChanged` runs after a change to the corrections. */
function useCorrections(onChanged: () => void) {
  const [rotation, setRotation] = useState(0);
  const [presetId, setPresetId] = useState<PresetId>(DEFAULT_PRESET);
  const [settings, setSettings] = useState<Enhancements>(presetById(DEFAULT_PRESET).settings);

  const change = (next: { preset?: PresetId; settings?: Enhancements }): void => {
    if (next.preset) setPresetId(next.preset);
    if (next.settings) setSettings(next.settings);
    onChanged();
  };
  const reset = (): void => {
    setPresetId(DEFAULT_PRESET);
    setSettings(presetById(DEFAULT_PRESET).settings);
    setRotation(0);
  };
  return {
    rotation,
    setRotation,
    presetId,
    settings,
    mode: presetById(presetId).mode,
    change,
    reset,
  };
}

/** «Migliora con IA»: its state, and the call that sends the corrected image out. */
function useAiEnhance() {
  const [ai, setAi] = useState<AiState>({ status: 'idle' });
  const [useAiResult, setUseAiResult] = useState(false);

  const clear = (): void => {
    setAi({ status: 'idle' });
    setUseAiResult(false);
  };
  /** A change to the corrections: a finished AI result was made from the old image. */
  const invalidate = (): void => {
    setAi((current) => (current.status === 'done' ? { status: 'idle' } : current));
    setUseAiResult(false);
  };
  const send = async (exportJpeg: () => Promise<string | null>): Promise<void> => {
    setAi({ status: 'working' });
    const jpeg = await exportJpeg();
    if (!jpeg) {
      setAi({ status: 'error', message: 'failed' });
      return;
    }
    const outcome = await enhanceWithAi(jpeg);
    if (outcome.kind === 'ok') {
      setAi({ status: 'done', dataUrl: outcome.dataUrl });
      setUseAiResult(false);
    } else {
      setAi({ status: 'error', message: outcome.kind });
    }
  };
  return { ai, setAi, useAiResult, setUseAiResult, clear, invalidate, send };
}

function useLoader(session: Session, onLoaded: () => void) {
  const [loadError, setLoadError] = useState(false);
  const loadFile = async (file: File): Promise<void> => {
    setLoadError(false);
    try {
      await session.load(file);
      onLoaded();
    } catch {
      setLoadError(true);
    }
  };
  const onFile = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) await loadFile(file);
  };
  const onDrop = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) void loadFile(file);
  };
  return { loadError, onFile, onDrop };
}

/** The «Immagini» flow: the step, the corrections, the AI option and the actions between steps. */
export function useImagesFlow() {
  const session = useImageSession();
  const [step, setStep] = useState<ImageStep>('load');
  const [format, setFormat] = useState<Format>('jpeg');
  const [result, setResult] = useState<string | null>(null);
  const [detectMissed, setDetectMissed] = useState(false);
  const aiState = useAiEnhance();
  const { ai, useAiResult } = aiState;
  const corr = useCorrections(aiState.invalidate);
  const { rotation, settings, mode } = corr;
  const loader = useLoader(session, () => {
    corr.setRotation(0);
    aiState.clear();
    setStep('straighten');
  });

  const params: RenderParams | null = useMemo(
    () => (session.corners ? { corners: session.corners, rotation, settings, mode } : null),
    [session.corners, rotation, settings, mode],
  );
  const exportJpeg = () => session.exportJpeg({ rotation, settings, mode });

  const goToDownload = async (): Promise<void> => {
    setResult(useAiResult && ai.status === 'done' ? ai.dataUrl : await exportJpeg());
    setStep('download');
  };
  const startOver = (): void => {
    session.reset();
    setResult(null);
    aiState.clear();
    corr.reset();
    setStep('load');
  };
  const move = (by: 1 | -1): void => {
    const next = IMAGE_STEPS[IMAGE_STEPS.indexOf(step) + by]!;
    if (by === 1 && step === 'correct') void goToDownload();
    else if (by === -1 && step === 'straighten') startOver();
    else setStep(next);
  };

  return {
    session,
    step,
    params,
    format,
    setFormat,
    result,
    detectMissed,
    setDetectMissed,
    corr,
    ai: aiState,
    loader,
    startOver,
    move,
    sendToAi: () => (params ? aiState.send(exportJpeg) : Promise.resolve()),
    download: () =>
      result ? saveImage(result, format, session.image?.name ?? 'immagine') : Promise.resolve(),
  };
}

export type ImagesFlow = ReturnType<typeof useImagesFlow>;
